import { WARP_ME_EXTERNAL_LINKS } from "@warpme/ui"
import { Menu } from "lucide-react"
import type { FC } from "react"
import { useNavigate } from "react-router"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { auth } from "@/lib/firebase"

type TenantPublicMenuProps = {
  shouldSignOutBeforeNavigation?: boolean
}

const SUPPORT_LINKS = [
  { href: WARP_ME_EXTERNAL_LINKS.support.tenantFaq, label: "よくあるご質問" },
  { href: WARP_ME_EXTERNAL_LINKS.support.company, label: "運営会社について" },
  { href: WARP_ME_EXTERNAL_LINKS.support.contact, label: "お問い合わせ" },
] as const

const OTHER_LINKS = [
  { href: WARP_ME_EXTERNAL_LINKS.legal.tenantTerms, label: "利用規約" },
  { href: WARP_ME_EXTERNAL_LINKS.legal.privacyPolicy, label: "プライバシーポリシー" },
  {
    href: WARP_ME_EXTERNAL_LINKS.legal.specifiedCommercialTransactions,
    label: "特定商取引法に基づく表記",
  },
] as const

export const TenantPublicMenu: FC<TenantPublicMenuProps> = ({
  shouldSignOutBeforeNavigation = false,
}) => {
  const navigate = useNavigate()

  const handleNavigate = async (to: "/signup" | "/login") => {
    if (shouldSignOutBeforeNavigation) {
      await auth.signOut()
    }

    navigate(to)
  }

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-12 rounded-full bg-highlight text-text backdrop-blur-[10px] hover:bg-highlight"
          aria-label="メニュー"
        >
          <Menu className="size-6" />
        </Button>
      </DrawerTrigger>
      <DrawerContent side="right" className="w-[315px] p-0">
        <DrawerHeader className="border-b border-border px-4 py-3 pr-12">
          <DrawerTitle>メニュー</DrawerTitle>
        </DrawerHeader>

        <div className="border-b border-border bg-muted/40 px-4 py-3">
          <div className="mb-3 text-xs text-muted-foreground">サポート</div>
          <div className="flex flex-col gap-3">
            {SUPPORT_LINKS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold leading-[1.5] text-foreground"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        <div className="border-b border-border px-4 py-3">
          <div className="mb-3 text-xs text-muted-foreground">その他</div>
          <div className="flex flex-col gap-3">
            {OTHER_LINKS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="text-sm leading-[1.5] text-foreground"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3 px-4 py-4">
          <Button
            type="button"
            variant="outline-secondary"
            className="w-full"
            onClick={() => void handleNavigate("/signup")}
          >
            店舗アカウントを登録する
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => void handleNavigate("/login")}
          >
            店舗アカウントにログインする
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
